import React, { useContext, useState } from "react";
import { Modal } from "react-bootstrap";
import useTranslator from "../../hooks/useTranslator";
import Cancelimg from "../../assets/img/cancel-01.svg";
import FluentShield from "../../assets/img/fluent_shield.svg";
import { formatPropertyAddress, getUserDisplayName } from "../../helper/common";
import InsuranceShieldIcon from "./InsuranceShieldIcon";
import SkorProtectBadge from "../SkorProtectBadge/SkorProtectBadge";
import SkorProtectShield from "../../assets/img/skor-protect-shield.png";
import { DownloadIcon, LightningIcon, MailIcon } from "../../helper/svgHelper";
import AuthContext from "../../context/auth/AuthContext";
import constants from "../../Utils/constants";
import LoadingSpinner from "../LoadingSpinner";
import { FiMessageSquare } from "react-icons/fi";

const AwardInsuranceConfirmPopup = ({
  show,
  onHide,
  onConfirm,
  selectedTenant,
  property,
  currentLang,
  isInsuranceToggleEnabled,
  preFilledInsuranceDocument,
  isInsurable,
  errorStatus,
  errorCode,
  insuranceRetryLoading,
  pricingPreview,
  onRetryInsurance,
  skorProtectLoading,
  onNotifyManualReview,
  isManualReviewNotified,
  manualReviewNotifyLoading,
}) => {
  const { _t } = useTranslator();
  const { user } = useContext(AuthContext);
  const [selection, setSelection] = useState(null); // null | "insurance" | "skor_protect"
  const [isConfirming, setIsConfirming] = useState(false);

  const renderDevelopmentPricing = (preview) => {
    if (!preview) return null;
    const total = Number(preview.total);
    const coverageMonths = Number(preview.coverageMonths) || 12;
    const coverageLabel = String(_t("twelveMonthCoverage")).replace(
      /\b12\b/,
      String(coverageMonths)
    );

    return (
      <div className="aicp-development-pricing">
        <span>{Number.isFinite(total) ? `€${total.toFixed(2)}` : "-"}</span>
        <span>{coverageLabel}</span>
      </div>
    );
  };

  const propertyName = formatPropertyAddress(property, currentLang);

  const specificInsuranceError = (() => {
    if (
      ["NIVEL3_ERR010", "NIVEL3_ERR016", "NIVEL3_ERR022"].includes(errorCode)
    ) {
      return {
        icon: "/images/insurance-not-available.svg",
        title: _t("insuranceUnavailableForThisRent"),
        description: _t("insuranceUnavailableForThisRentDescription"),
        hint: _t("awardWithoutProtectionOrLowerRent"),
      };
    }
    if (["NIVEL3_ERR043", "NIVEL3_ERR053"].includes(errorCode)) {
      return {
        icon: "/images/property-already-insured.svg",
        title: _t("propertyAlreadyInsured"),
        description: _t("propertyAlreadyInsuredDescription"),
        hint: _t("awardWithoutProtectionExistingPolicyHint"),
      };
    }
    if (["NIVEL3_ERR085", "NIVEL3_ERR093"].includes(errorCode)) {
      return {
        icon: "/images/tenant-already-request.svg",
        title: _t("tenantAlreadyInAnotherInsuranceRequest"),
        description: _t("tenantAlreadyInAnotherInsuranceRequestDescription"),
        hint: _t("awardWithoutProtectionDuplicateRequestHint"),
      };
    }
    return null;
  })();

  const tenantOptedInSkorProtect = selectedTenant?.some(
    (t) => t?.tenantid?.skor_protect || t?.application_id?.skor_protect
  );

  const handleSelect = (type) => {
    setSelection((prev) => (prev === type ? null : type));
  };

  const handleConfirm = async () => {
    if (isConfirming) return;

    const decision =
      selection === "insurance"
        ? "insurance"
        : selection === "skor_protect"
          ? "skor_protect_flow"
          : "direct_award";

    setIsConfirming(true);
    try {
      const shouldClose = await onConfirm(decision);
      if (shouldClose !== false) {
        onHide();
      }
    } finally {
      setIsConfirming(false);
    }
  };

  const tenants = selectedTenant?.flatMap((s) => {
    const isApplication = s?.application_id?.participants?.length > 0;
    return isApplication
      ? s?.application_id?.participants?.map((p) => p?.tenant_info)
      : s?.tenantid;
  });

  return (
    <Modal
      show={show}
      onHide={() => {
        if (!isConfirming) onHide();
      }}
      centered
      backdrop={isConfirming ? "static" : true}
      keyboard={!isConfirming}
      className="newmodalui wrapper-modal inviteTenantModal max-w-lg awardInsuranceConfirmModal"
    >
      <Modal.Header>
        <h2 className="aic-title">{_t("awardProperty")}</h2>
        <div
          className="cursor-pointer"
          onClick={() => {
            if (!isConfirming) onHide();
          }}
        >
          <img src={Cancelimg} alt="close" className="canceicon topRightIcon" />
        </div>
      </Modal.Header>
      <Modal.Body>
        <p className="aic-desc">
          {_t("youAreAboutToAward")}{" "}
          <span className="aic-prop-name">{propertyName}</span>{" "}
          {_t("toTheFollowingTenants")}:
        </p>

        {tenants?.length > 0 && (
          <div className="aic-chips">
            {tenants?.map((tenant, idx) => (
              <span key={idx} className="aic-chip">
                {getUserDisplayName(tenant) || tenant?.tenantid?.label || ""}
              </span>
            ))}
          </div>
        )}

        {!isInsurable &&
          (errorStatus === constants.INSURANCE_ERROR_STATUS.MANUAL_REVIEW ||
            errorStatus === constants.INSURANCE_ERROR_STATUS.ERROR) && (
            <>
              <p className="aicp-section-label">
                {_t("protectYourRental")}{" "}
                <span className="aicp-optional">({_t("optional")})</span>
              </p>

              {specificInsuranceError ? (
                <>
                  <div className="aic-status-banner aic-status-banner--specific">
                    <img src={specificInsuranceError.icon} alt="" />
                    <div className="aic-status-banner-text">
                      <p className="aic-status-banner-title">
                        {specificInsuranceError.title}
                      </p>
                      <p className="aic-status-banner-desc">
                        {specificInsuranceError.description}
                      </p>
                    </div>
                  </div>
                  <p className="aic-support-line">
                    <FiMessageSquare color="#253837" />
                    {_t("needHelp")}{" "}
                    <a
                      href="https://wa.me/34617662602"
                      className="aic-support-link"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      target="_blank"
                    >
                      {_t("contactSupport")}
                    </a>
                  </p>
                  <p className="aicp-hint">{specificInsuranceError.hint}</p>
                </>
              ) : errorStatus ===
                constants.INSURANCE_ERROR_STATUS.MANUAL_REVIEW ? (
                <div className="aic-status-banner aic-status-banner--warning">
                  <img src="/images/manual-review.svg" />
                  <div className="aic-status-banner-text">
                    <p className="aic-status-banner-title">
                      {_t(
                        "manualReviewByInsurer",
                        "Manual review by the insurer"
                      )}
                    </p>
                    <p className="aic-status-banner-desc">
                      {_t(
                        "manualReviewByInsurerDescription",
                        "Our insurer needs additional guarantees for this profile. This does not mean tenants are uninsurable, just that further review is required."
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="aic-status-banner aic-status-banner--neutral">
                    {insuranceRetryLoading ? (
                      <div className="aic-skeleton aic-skeleton-icon" />
                    ) : (
                      <img src="/images/could-not-check-insurance.svg" alt="" />
                    )}
                    <div className="aic-status-banner-text">
                      {insuranceRetryLoading ? (
                        <>
                          <div className="aic-skeleton aic-skeleton-title" />
                          <div className="aic-skeleton aic-skeleton-desc" />
                        </>
                      ) : (
                        <>
                          <p className="aic-status-banner-title">
                            {_t("couldNotCheckInsuranceRightNow")}
                          </p>
                          <p className="aic-status-banner-desc">
                            {_t("genericInsuranceCheckError")}
                          </p>
                        </>
                      )}
                      <button
                        type="button"
                        className="aic-retry-btn"
                        disabled={insuranceRetryLoading}
                        onClick={onRetryInsurance}
                      >
                        {insuranceRetryLoading ? (
                          <LoadingSpinner
                            size="14px"
                            color="#737f7b"
                            borderWidth={2}
                          />
                        ) : (
                          <svg
                            className="aic-retry-icon"
                            viewBox="0 0 24 24"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M4 4v6h6M4.7 15a8 8 0 1 0 .2-6.4"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                        {_t("tryAgain")}
                      </button>
                    </div>
                  </div>
                  {!insuranceRetryLoading && (
                    <p className="aicp-hint">
                      {_t("awardNowAndAddInsuranceLater")}
                    </p>
                  )}
                </>
              )}

              {!specificInsuranceError &&
                errorStatus ===
                  constants.INSURANCE_ERROR_STATUS.MANUAL_REVIEW && (
                  <>
                    <button
                      type="button"
                      className="aic-notify-btn"
                      disabled={
                        isManualReviewNotified || manualReviewNotifyLoading
                      }
                      onClick={onNotifyManualReview}
                    >
                      <MailIcon size={16} color="#253837" />
                      {isManualReviewNotified
                        ? _t(
                            "insuranceManualReviewNotificationSent",
                            "We've notified you're still interested in insurance"
                          )
                        : manualReviewNotifyLoading
                          ? _t("sending", "Sending...")
                          : _t(
                              "notifyStillInterestedInInsurance",
                              "Notify us you're still interested in insurance"
                            )}
                    </button>
                    <p className="aicp-hint">
                      {_t(
                        "awardWithoutProtectionOrWaitForReview",
                        "Award without protection or wait for the insurer's review."
                      )}
                    </p>
                  </>
                )}
            </>
          )}

        {isInsurable &&
          (tenantOptedInSkorProtect ||
          user?.current_workspace?.skor_protect_service === true ? (
            <>
              <p className="aicp-section-label">
                {_t("protectYourRental")}{" "}
                <span className="aicp-optional">({_t("optional")})</span>
              </p>

              <div className="aicp-cards-row">
                {/* Insurance card */}

                <button
                  className={`aicp-card${selection === "insurance" ? " aicp-card--ins-selected" : ""}`}
                  onClick={() => handleSelect("insurance")}
                >
                  <div className="aicp-card-header">
                    <div className="aicp-card-icon-title">
                      <InsuranceShieldIcon size={18} color={"#AB8D5C"} />
                      <span className="aicp-card-title">{_t("insurance")}</span>
                    </div>
                    <div
                      className={`aicp-radio${selection === "insurance" ? " aicp-radio--ins" : ""}`}
                    />
                  </div>
                  <p className="aicp-card-desc">
                    {_t("insuranceCardDescription")}
                  </p>
                  <span className="aicp-card-payer">
                    {_t("paidByLandlord")}
                  </span>
                  {renderDevelopmentPricing(pricingPreview?.insurance)}
                </button>

                {/* Skor Protect card */}
                <button
                  className={`aicp-card aicp-card-sp${selection === "skor_protect" ? " aicp-card--sp-selected" : ""}`}
                  onClick={() => handleSelect("skor_protect")}
                >
                  <div className="aicp-card-header">
                    <div className="aicp-card-icon-title">
                      <img
                        src={SkorProtectShield}
                        alt=""
                        className="sp-shield"
                      />
                      <span className="aicp-card-title">
                        {_t("skorProtectTitle")}
                      </span>
                    </div>
                    <div
                      className={`aicp-radio${selection === "skor_protect" ? " aicp-radio--sp" : ""}`}
                    />
                  </div>
                  <p className="aicp-card-desc">
                    {_t("skorProtectCardDescription")}
                  </p>
                  <div className="aicp-card-footer-row">
                    <span className="aicp-card-payer">
                      {_t("paidByTenant")}
                    </span>
                    {tenantOptedInSkorProtect && (
                      <span className="aicp-tenant-opted-badge">
                        {_t("tenantOptedIn")}
                      </span>
                    )}
                  </div>
                  {renderDevelopmentPricing(pricingPreview?.skorProtect)}
                </button>
              </div>

              {/* What happens next — Insurance */}
              {selection === "insurance" && (
                <div className="aicp-next-box aicp-next-box--ins">
                  <p className="aicp-next-label">{_t("whatHappensNext")}</p>
                  <ol className="aicp-next-steps">
                    <li>
                      <span className="aicp-step-num aicp-step-num--ins">
                        1
                      </span>
                      {_t("insuranceAwardStep1")}
                    </li>
                    <li>
                      <span className="aicp-step-num aicp-step-num--ins">
                        2
                      </span>
                      {_t("insuranceAwardStep2")}
                    </li>
                    <li>
                      <span className="aicp-step-num aicp-step-num--ins">
                        3
                      </span>
                      {_t("insuranceAwardStep3")}
                    </li>
                  </ol>
                </div>
              )}

              {/* What happens next — Skor Protect */}
              {selection === "skor_protect" && (
                <div className="aicp-next-box aicp-next-box--sp">
                  <p className="aicp-next-label">{_t("whatHappensNext")}</p>
                  <ol className="aicp-next-steps">
                    <li>
                      <span className="aicp-step-num aicp-step-num--sp">1</span>
                      {_t("skorProtectAwardStep1")}
                    </li>
                    <li>
                      <span className="aicp-step-num aicp-step-num--sp">2</span>
                      {_t("skorProtectAwardStep2")}
                    </li>
                    <li>
                      <span className="aicp-step-num aicp-step-num--sp">3</span>
                      {_t("skorProtectAwardStep3")}
                    </li>
                  </ol>
                </div>
              )}

              <p className="aicp-hint">{_t("selectOneOrLeaveUnselected")}</p>
            </>
          ) : (
            <div className="aic-insurance-card">
              <div className="aic-insurance-icon">
                <LightningIcon />
              </div>
              <div className="aic-insurance-text">
                <p className="aic-insurance-title">
                  {isInsuranceToggleEnabled
                    ? _t("addSkorRentDefaultInsurance")
                    : _t("addSkorRentDefaultInsuranceTogglerDisabled")}
                </p>
                <p className="aic-insurance-subtitle">
                  {isInsuranceToggleEnabled
                    ? _t("insuranceContractingDescription")
                    : _t("insuranceContractingDescriptionTogglerDisabled")}
                </p>
                {preFilledInsuranceDocument && (
                  <a
                    href={`${preFilledInsuranceDocument}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="aic-insurance-download-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DownloadIcon />
                    {_t("downloadInsuranceProjectTemplate")}
                  </a>
                )}
              </div>
              <label className="aic-toggle">
                <input
                  type="checkbox"
                  checked={selection === "insurance"}
                  onChange={() => {
                    setSelection((prev) =>
                      prev === "insurance" ? null : "insurance"
                    );
                  }}
                />
                <span className="aic-toggle-slider" />
              </label>
            </div>
          ))}
      </Modal.Body>
      <Modal.Footer className="aic-footer">
        <button
          className="aic-btn-cancel"
          onClick={onHide}
          disabled={isConfirming}
          type="button"
        >
          {_t("cancel")}
        </button>
        <button
          className="btnNext aic-btn-award"
          disabled={isConfirming || skorProtectLoading || insuranceRetryLoading}
          onClick={handleConfirm}
          type="button"
        >
          {isConfirming ? (
            <>
              <LoadingSpinner size="14px" color="#253837" borderWidth={2} />
              {_t("processing")}
            </>
          ) : (
            <>
              {(selection === "insurance" || selection === "skor_protect") && (
                <img src={FluentShield} alt="" />
              )}
              {_t("awardProperty")}
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AwardInsuranceConfirmPopup;
